import { getServiceLabels } from '@/lib/form-state/util'
import { SearchByNumberModal } from '@/components/global-search/SearchByNumberModal'
import {
    getPinnedModules,
    updatePinnedModules,
} from '@/lib/api/utils/pinned-modules-api'
import {
    getSystemModules,
    SystemModule,
} from '@/lib/api/system-modules/system-modules-api'
import { usePermission } from '@/lib/hooks/use-permission'
import { getFileUrl } from '@/lib/utils/file-url'
import BillingsFormPage from '@/pages/admin/financial/billings/billings-form-page'
import { useAuth } from '@/store/hooks'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { HiSparkles } from 'react-icons/hi2'
import {
    MdDashboard,
    MdExpandLess,
    MdExpandMore,
    MdLocalHospital,
    MdOutlineEmail,
    MdPerson,
    MdPushPin,
    MdOutlinePushPin,
    MdReceipt,
    MdLinkOff,
    MdSettings,
    MdCheck,
} from 'react-icons/md'
import { useSidebarTheme } from '@/lib/hooks/use-sidebar-theme'
import type { SidebarTheme } from '@/lib/api/utils/sidebar-theme-api'
import { useOrphanPaymentsCount } from '@/lib/hooks/use-orphan-payments-count'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { isIndividualCompany } from '@/lib/person-type'
import {
    HIDE_MODULES_FOR_INDIVIDUAL,
    HIDE_RESOURCE_BY_SECONDARY_SEGMENT,
    SIDEBAR_MODULE_CONFIG,
} from './sidebar-config'
import {
    isResourceHiddenForSegment,
    moduleIcon,
    moduleLabel,
    resourceIcon,
    resourceLabel,
} from './segment-labels'

interface MenuItem {
    label: string
    icon: any
    href: string
    badge?: string | null
    permission?: { module: string; resource: string }
}

interface MenuModule {
    label: string
    labelComponent?: React.ReactNode
    icon: any
    items: MenuItem[]
    permissionModule?: string
    moduleKey?: string
}

const inboxMenuItem: MenuItem = {
    label: 'Emails',
    icon: MdOutlineEmail,
    href: '/administrative/inbox',
}

const accountMenuItem: MenuItem = {
    label: 'Minha Conta',
    icon: MdPerson,
    href: '/account',
}

const aiAssistantMenuItem: MenuItem = {
    label: 'Assistente IA',
    icon: HiSparkles,
    href: '/chat-interno?tab=ai',
}

interface AdminSidebarProps {
    onItemClick?: () => void
    collapsed?: boolean
}

export const AdminSidebar = ({
    onItemClick,
    collapsed = false,
}: AdminSidebarProps) => {
    const location = useLocation()
    const navigate = useNavigate()
    const [medicalDropdownPos, setMedicalDropdownPos] = useState<{
        left: number
        bottom: number
    } | null>(null)
    const [showBillingModal, setShowBillingModal] = useState(false)
    const [showMedicalDropdown, setShowMedicalDropdown] = useState(false)
    const [showSearchByNumber, setShowSearchByNumber] = useState(false)
    const [flyoutModule, setFlyoutModule] = useState<string | null>(null)
    const [flyoutTop, setFlyoutTop] = useState(0)
    const [flyoutAdjustedTop, setFlyoutAdjustedTop] = useState<number | null>(
        null
    )
    const flyoutRef = useRef<HTMLDivElement>(null)
    // Tooltip da barra recolhida (só ícones). Em portal com posição fixa: dentro da barra
    // o `overflow` o cortaria, e o `title` nativo demora a aparecer.
    const [collapsedTip, setCollapsedTip] = useState<{ label: string; top: number; left: number } | null>(null)
    const showCollapsedTip = (label: string, target: HTMLElement) => {
        const rect = target.getBoundingClientRect()
        setCollapsedTip({ label, top: rect.top + rect.height / 2, left: rect.right + 10 })
    }
    const hideCollapsedTip = () => setCollapsedTip(null)
    // Clicou e navegou: o ícone pode sair de baixo do mouse sem disparar `mouseleave`.
    useEffect(() => setCollapsedTip(null), [location.pathname, collapsed])
    const sidebarRef = useRef<HTMLElement>(null)
    const moduleButtonRefs = useRef<Record<string, HTMLButtonElement | null>>(
        {}
    )
    const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 1024)

    useEffect(() => {
        const mq = window.matchMedia('(min-width: 1024px)')
        const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches)
        mq.addEventListener('change', handler)
        return () => mq.removeEventListener('change', handler)
    }, [])
    const { user, isMaster } = useAuth()
    const { theme: sidebarTheme, setTheme: setSidebarTheme, themeClass } =
        useSidebarTheme(user?.id)
    const isLightSidebar = sidebarTheme === 'light'
    const [themeMenuPos, setThemeMenuPos] = useState<{
        left: number
        bottom: number
    } | null>(null)
    const { canAccessModule, canRead } = usePermission()
    // Pix que entrou fora de qualquer cobrança e ainda não tem dono. Quem concilia é
    // quem lê o Extrato; o botão só aparece quando há algum, para chamar a atenção
    // antes que alguém registre o mesmo valor à mão.
    const orphanPaymentsCount = useOrphanPaymentsCount(canRead('financial', 'ledger'))
    const segment = user?.company?.segment || 'OTHER'
    const secondarySegment = user?.company?.secondarySegment || 'OTHER'
    const planModules = user?.planModules as
        | Record<string, string[]>
        | undefined

    const [systemModules, setSystemModules] = useState<SystemModule[]>([])
    const [loadingModules, setLoadingModules] = useState(true)
    const [loadError, setLoadError] = useState(false)
    const [pinnedModules, setPinnedModules] = useState<string[]>([])

    useEffect(() => {
        let cancelled = false
        getPinnedModules()
            .then(res => {
                if (
                    !cancelled &&
                    res.success &&
                    res.result &&
                    Array.isArray(res.result.moduleKeys)
                ) {
                    setPinnedModules(res.result.moduleKeys)
                }
            })
            .catch(() => {})
        return () => {
            cancelled = true
        }
    }, [])

    const togglePinnedModule = useCallback((moduleKey: string) => {
        setPinnedModules(prev => {
            const next = prev.includes(moduleKey)
                ? prev.filter(k => k !== moduleKey)
                : [...prev, moduleKey]
            updatePinnedModules(next).catch(() => {
                setPinnedModules(prev)
            })
            return next
        })
    }, [])

    useEffect(() => {
        let cancelled = false
        const load = async () => {
            try {
                const response = await getSystemModules()

                if (!cancelled && response.success && response.result) {
                    setSystemModules(response.result)
                    setLoadError(false)
                }
            } catch {
                if (!cancelled) setLoadError(true)
            } finally {
                if (!cancelled) setLoadingModules(false)
            }
        }
        load()
        return () => {
            cancelled = true
        }
    }, [])

    const companyName = user?.partner?.name || user?.company?.name || ''

    // Conta de pessoa física não emite nota — o módulo fiscal sai do menu em vez de
    // aparecer desabilitado, que só convidaria a pedir suporte para algo que a
    // integração não faz.
    const isCompanyIndividual = isIndividualCompany(user?.company)

    const buildMenuModules = useCallback((): MenuModule[] => {
        return systemModules
            .filter(mod => mod.key !== 'dashboard')
            .filter(
                mod =>
                    !isCompanyIndividual ||
                    !HIDE_MODULES_FOR_INDIVIDUAL.includes(mod.key)
            )
            .map(mod => {
                const config = SIDEBAR_MODULE_CONFIG[mod.key]
                if (!config) return null

                const items: MenuItem[] = mod.resources
                    .map(res => {
                        const resConfig = config.resources[res.key]

                        if (
                            HIDE_RESOURCE_BY_SECONDARY_SEGMENT[
                                secondarySegment
                            ]?.includes(res.key)
                        ) {
                            return null
                        }

                        if (isResourceHiddenForSegment(segment, res.key)) {
                            return null
                        }

                        if (!resConfig) return null
                        // "Pacientes" só na saúde direta; a intermediação atende
                        // clínicas, não pacientes. Fora daí, o vocabulário de cada
                        // segmento vem de `segment-labels.ts`.
                        const label =
                            segment === 'HEALTH' &&
                            secondarySegment !== 'INTERMEDIATION' &&
                            res.key === 'clients'
                                ? 'Pacientes'
                                : resourceLabel(
                                      segment,
                                      mod.key,
                                      res.key,
                                      res.label
                                  )
                        return {
                            label,
                            icon: resourceIcon(
                                segment,
                                mod.key,
                                res.key,
                                resConfig.icon
                            ),
                            href: resConfig.href,
                            badge: null,
                            permission: { module: mod.key, resource: res.key },
                        } as MenuItem
                    })
                    .filter(Boolean) as MenuItem[]

                return {
                    label: moduleLabel(segment, mod.key, mod.label),
                    icon: moduleIcon(segment, mod.key, config.icon),
                    permissionModule: mod.key,
                    moduleKey: mod.key,
                    items,
                } as MenuModule
            })
            .filter(Boolean) as MenuModule[]
    }, [systemModules, companyName, isCompanyIndividual])

    const menuModules = useMemo(() => {
        const all = buildMenuModules()
        const pinnedSet = new Set(pinnedModules)
        const pinned: MenuModule[] = []
        const unpinned: MenuModule[] = []
        for (const m of all) {
            if (m.moduleKey && pinnedSet.has(m.moduleKey)) {
                pinned.push(m)
            } else {
                unpinned.push(m)
            }
        }
        pinned.sort(
            (a, b) =>
                pinnedModules.indexOf(a.moduleKey!) -
                pinnedModules.indexOf(b.moduleKey!)
        )
        return [...pinned, ...unpinned]
    }, [buildMenuModules, pinnedModules])

    // Dashboard como single item (busca label do backend)
    const dashboardItem = useMemo((): MenuItem => {
        const dashMod = systemModules.find(m => m.key === 'dashboard')
        return {
            label: dashMod?.label || 'Dashboard',
            icon: MdDashboard,
            href: '/dashboard',
            badge: null,
            permission: { module: 'dashboard', resource: 'view' },
        }
    }, [systemModules])

    const isModuleActive = (items: MenuItem[]) => {
        return items.some(item => {
            const exactMatch = location.pathname === item.href
            const prefixMatch =
                item.href !== '/' &&
                item.href !== '/admin' &&
                location.pathname.startsWith(item.href + '/')
            return exactMatch || prefixMatch
        })
    }

    const isModuleInPlan = (moduleKey: string): boolean => {
        if (isMaster) return true
        if (!planModules || typeof planModules !== 'object') return false
        return moduleKey in planModules
    }

    const isItemVisible = (item: MenuItem): boolean => {
        if (item.permission) {
            if (!isModuleInPlan(item.permission.module)) return false
            return canRead(item.permission.module, item.permission.resource)
        }
        return true
    }

    const isModuleVisible = (module: MenuModule): boolean => {
        if (module.permissionModule) {
            if (!isModuleInPlan(module.permissionModule)) return false
            return canAccessModule(module.permissionModule)
        }
        return true
    }

    const initialOpenModules = useMemo(() => {
        return menuModules
            .filter(module => isModuleActive(module.items))
            .map(module => module.label)
    }, [location.pathname, menuModules])

    const [openModules, setOpenModules] = useState<string[]>(initialOpenModules)

    useEffect(() => {
        const activeModules = menuModules
            .filter(module => isModuleActive(module.items))
            .map(module => module.label)

        setOpenModules(prev => {
            const newModules = [...prev]
            activeModules.forEach(m => {
                if (!newModules.includes(m)) {
                    newModules.push(m)
                }
            })
            return newModules
        })
    }, [location.pathname, menuModules])

    const toggleModule = (moduleLabel: string) => {
        setOpenModules(prev =>
            prev.includes(moduleLabel)
                ? prev.filter(m => m !== moduleLabel)
                : [...prev, moduleLabel]
        )
    }

    // Ajustar posição do flyout para não ultrapassar a tela
    useEffect(() => {
        if (!flyoutModule || !flyoutRef.current || !sidebarRef.current) {
            setFlyoutAdjustedTop(null)
            return
        }
        const sidebarTop = sidebarRef.current.getBoundingClientRect().top
        const idealTop = flyoutTop + sidebarTop
        const flyoutHeight = flyoutRef.current.offsetHeight
        const viewportHeight = window.innerHeight
        const margin = 12

        if (idealTop + flyoutHeight > viewportHeight - margin) {
            setFlyoutAdjustedTop(
                Math.max(margin, viewportHeight - flyoutHeight - margin)
            )
        } else {
            setFlyoutAdjustedTop(idealTop)
        }
    }, [flyoutModule, flyoutTop])

    // Fechar flyout ao clicar fora
    useEffect(() => {
        if (!flyoutModule) return
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node
            if (
                flyoutRef.current &&
                !flyoutRef.current.contains(target) &&
                !Object.values(moduleButtonRefs.current).some(btn =>
                    btn?.contains(target)
                )
            ) {
                setFlyoutModule(null)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () =>
            document.removeEventListener('mousedown', handleClickOutside)
    }, [flyoutModule])

    // Fechar flyout ao mudar de rota
    useEffect(() => {
        setFlyoutModule(null)
    }, [location.pathname])

    const handleModuleClick = (
        moduleLabel: string,
        buttonEl: HTMLButtonElement | null,
        hasActiveItem: boolean
    ) => {
        // Menu em ícones: o accordion não existe (não há onde a lista se abrir),
        // então é SEMPRE o flyout — inclusive no módulo que está ativo. Sem esta
        // saída, clicar no módulo aberto só alternava um estado invisível e a
        // impressão era de botão morto.
        if (collapsed && isDesktop && !onItemClick) {
            if (flyoutModule === moduleLabel) {
                setFlyoutModule(null)
                return
            }
            if (buttonEl && sidebarRef.current) {
                const btnRect = buttonEl.getBoundingClientRect()
                const sidebarRect = sidebarRef.current.getBoundingClientRect()
                setFlyoutTop(btnRect.top - sidebarRect.top)
            }
            setFlyoutModule(moduleLabel)
            return
        }
        // Mobile ou módulo com item ativo: accordion
        if (!isDesktop || onItemClick || hasActiveItem) {
            toggleModule(moduleLabel)
            setFlyoutModule(null)
            return
        }
        // Desktop sem item ativo: flyout lateral
        if (flyoutModule === moduleLabel) {
            setFlyoutModule(null)
            return
        }
        if (buttonEl && sidebarRef.current) {
            const btnRect = buttonEl.getBoundingClientRect()
            const sidebarRect = sidebarRef.current.getBoundingClientRect()
            setFlyoutTop(btnRect.top - sidebarRect.top)
        }
        setFlyoutModule(moduleLabel)
    }

    const renderMenuItem = (item: MenuItem, parentItems?: MenuItem[]) => {
        const Icon = item.icon
        const hrefPath = item.href.split('?')[0]
        const hrefSearch = item.href.includes('?')
            ? '?' + item.href.split('?')[1]
            : ''

        const isExactMatch = hrefSearch
            ? location.pathname === hrefPath && location.search === hrefSearch
            : location.pathname === hrefPath

        const isPrefixMatch =
            hrefPath !== '/admin' &&
            hrefPath !== '/' &&
            !hrefSearch &&
            location.pathname.startsWith(hrefPath + '/')

        let hasMoreSpecificMatch = false
        if (parentItems) {
            hasMoreSpecificMatch = parentItems.some(sibling => {
                if (sibling.href === item.href) return false
                if (sibling.href.length <= item.href.length) return false
                const siblingExactMatch = location.pathname === sibling.href
                const siblingPrefixMatch =
                    sibling.href !== '/admin' &&
                    sibling.href !== '/' &&
                    location.pathname.startsWith(sibling.href + '/')
                return siblingExactMatch || siblingPrefixMatch
            })
        }

        const isActive =
            isExactMatch || (isPrefixMatch && !hasMoreSpecificMatch)

        if (collapsed) {
            return (
                <Link
                    key={item.href}
                    to={item.href}
                    onClick={onItemClick}
                    className={`
                        flex items-center justify-center w-10 h-10 mx-auto rounded-lg
                        transition-all duration-200 group relative
                        ${
                            isActive
                                ? 'bg-secondary text-primary-content shadow-md'
                                : 'text-base-content/70 hover:bg-base-200/70 hover:text-base-content'
                        }
                    `}
                    aria-label={item.label}
                    onMouseEnter={e => showCollapsedTip(item.label, e.currentTarget)}
                    onMouseLeave={hideCollapsedTip}
                    onFocus={e => showCollapsedTip(item.label, e.currentTarget)}
                    onBlur={hideCollapsedTip}
                >
                    <Icon className="text-lg" />
                </Link>
            )
        }

        return (
            <Link
                key={item.href}
                to={item.href}
                onClick={onItemClick}
                className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-lg
                    transition-all duration-200 group relative
                    ${
                        isActive
                            ? 'bg-secondary text-primary-content shadow-md'
                            : 'text-base-content hover:bg-base-200/70'
                    }
                `}
            >
                {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-primary-content rounded-r-full" />
                )}
                <Icon
                    className={`text-lg flex-shrink-0 ${
                        isActive
                            ? 'text-primary-content'
                            : 'text-base-content/70 group-hover:text-base-content'
                    }`}
                />
                <span
                    className={`font-normal text-[15px] flex-1 min-w-0 truncate ${
                        isActive
                            ? 'text-primary-content'
                            : 'text-base-content group-hover:text-base-content'
                    }`}
                >
                    {item.label}
                </span>
                {item.badge && (
                    <span className="badge badge-xs badge-secondary">
                        {item.badge}
                    </span>
                )}
            </Link>
        )
    }

    const renderSkeletonItems = () => (
        <div className="space-y-2 px-3">
            {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-3 py-2.5">
                    <div className="w-5 h-5 rounded bg-base-300/50 animate-pulse" />
                    <div
                        className="h-4 rounded bg-base-300/50 animate-pulse"
                        style={{ width: `${60 + Math.random() * 40}%` }}
                    />
                </div>
            ))}
        </div>
    )

    return (
        <aside
            ref={sidebarRef}
            className={`${collapsed ? 'w-16' : 'w-72'} h-screen border-r border-black/5 flex flex-col shadow-xl overflow-hidden transition-all duration-200 relative`}
        >
          {/* Rail do menu. O escopo `.sidebar-dark` / `.sidebar-light` (index.css)
              redefine as vars base só aqui, conforme o tema escolhido pela pessoa.
              Os modais no fim do componente ficam FORA deste wrapper — senão
              herdariam o fundo da rail. */}
          <div className={`${themeClass} flex flex-col flex-1 min-h-0 overflow-hidden`}>
            <div
                className={
                    collapsed ? 'p-3 pb-2 flex justify-center' : 'p-6 pb-4'
                }
            >
                <div
                    className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'}`}
                >
                    <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shadow-lg overflow-hidden flex-shrink-0">
                        <img
                            src={
                                user?.partner?.logoUrl || user?.company?.logoUrl
                                    ? getFileUrl(
                                          user?.partner?.logoUrl ||
                                              user?.company?.logoUrl
                                      )
                                    : '/logo192.png'
                            }
                            alt="Logo"
                            className="w-10 h-10 object-contain"
                        />
                    </div>
                    {!collapsed && (
                        <div className="min-w-0 flex-1">
                            <h1
                                className="text-lg font-bold text-base-content truncate cursor-pointer"
                                onClick={() => navigate('/')}
                            >
                                {user?.partner?.name ||
                                    user?.partner?.tradingName ||
                                    user?.company?.tradingName ||
                                    user?.company?.name ||
                                    'MediarDex'}
                            </h1>
                            <p className="text-xs text-base-content/60">
                                Painel de Controle
                            </p>
                        </div>
                    )}
                </div>
            </div>

            <nav
                className={`scroll-container flex-1 ${collapsed ? 'px-1' : 'px-3'} pb-4 overflow-y-auto`}
            >
                <div className={`${collapsed ? 'space-y-1' : 'space-y-0'}`}>
                    {loadingModules ? (
                        renderSkeletonItems()
                    ) : loadError ? (
                        <div className="px-3 py-4 text-center">
                            <p className="text-sm text-base-content/60">
                                Erro ao carregar menu
                            </p>
                            <button
                                className="btn btn-ghost btn-xs mt-2"
                                onClick={() => {
                                    setLoadingModules(true)
                                    setLoadError(false)
                                    getSystemModules()
                                        .then(res => {
                                            if (res.success && res.result) {
                                                setSystemModules(res.result)
                                            }
                                        })
                                        .catch(() => setLoadError(true))
                                        .finally(() => setLoadingModules(false))
                                }}
                            >
                                Tentar novamente
                            </button>
                        </div>
                    ) : (
                        <>
                            {isItemVisible(dashboardItem) &&
                                renderMenuItem(dashboardItem)}

                            {menuModules.filter(isModuleVisible).map(module => {
                                const ModuleIcon = module.icon
                                const isOpen = openModules.includes(
                                    module.label
                                )
                                const hasActiveItem = isModuleActive(
                                    module.items
                                )
                                const isFlyoutOpen =
                                    flyoutModule === module.label
                                const isMobileMode = !isDesktop || !!onItemClick

                                const filteredItems =
                                    module.items.filter(isItemVisible)

                                if (filteredItems.length === 0) return null

                                if (collapsed) {
                                    // Em ícones, o módulo abre a MESMA janelinha
                                    // lateral do menu expandido. Antes ele levava
                                    // direto ao primeiro recurso: quem quisesse
                                    // qualquer um dos outros passava por uma tela
                                    // que não pediu, e o menu escondia o que tinha
                                    // dentro.
                                    return (
                                        <button
                                            key={
                                                module.permissionModule ||
                                                module.label
                                            }
                                            ref={el => {
                                                moduleButtonRefs.current[
                                                    module.label
                                                ] = el
                                            }}
                                            onClick={e =>
                                                handleModuleClick(
                                                    module.label,
                                                    e.currentTarget,
                                                    hasActiveItem
                                                )
                                            }
                                            aria-expanded={isFlyoutOpen}
                                            className={`
                                                    flex items-center justify-center w-10 h-10 mx-auto rounded-lg
                                                    transition-all duration-200
                                                    ${
                                                        hasActiveItem || isFlyoutOpen
                                                            ? 'bg-secondary text-primary-content shadow-md'
                                                            : 'text-base-content/70 hover:bg-base-200/70 hover:text-base-content'
                                                    }
                                                `}
                                            aria-label={module.label}
                                            onMouseEnter={e => showCollapsedTip(module.label, e.currentTarget)}
                                            onMouseLeave={hideCollapsedTip}
                                            onFocus={e => showCollapsedTip(module.label, e.currentTarget)}
                                            onBlur={hideCollapsedTip}
                                        >
                                            <ModuleIcon className="text-lg" />
                                        </button>
                                    )
                                }

                                return (
                                    <div
                                        key={
                                            module.permissionModule ||
                                            module.label
                                        }
                                        className="space-y-0"
                                    >
                                        <button
                                            ref={el => {
                                                moduleButtonRefs.current[
                                                    module.label
                                                ] = el
                                            }}
                                            onClick={e =>
                                                handleModuleClick(
                                                    module.label,
                                                    e.currentTarget,
                                                    hasActiveItem
                                                )
                                            }
                                            className={`
                                                    w-full flex items-center gap-3 px-3 py-2.5 rounded-lg
                                                    transition-all duration-200 group
                                                    ${
                                                        hasActiveItem ||
                                                        isFlyoutOpen
                                                            ? isLightSidebar
                                                                ? 'bg-secondary/10 text-base-content'
                                                                : 'bg-white/10 text-white'
                                                            : 'text-base-content hover:bg-base-200/70'
                                                    }
                                                `}
                                        >
                                            <ModuleIcon
                                                className={`text-lg flex-shrink-0 ${
                                                    hasActiveItem ||
                                                    isFlyoutOpen
                                                        ? 'text-secondary'
                                                        : 'text-base-content/70 group-hover:text-base-content'
                                                }`}
                                            />
                                            <span
                                                className={`font-normal text-[15px] flex-1 min-w-0 text-left truncate ${
                                                    hasActiveItem ||
                                                    isFlyoutOpen
                                                        ? isLightSidebar
                                                            ? 'text-base-content font-medium'
                                                            : 'text-white'
                                                        : 'text-base-content group-hover:text-base-content'
                                                }`}
                                            >
                                                {module.labelComponent ||
                                                    module.label}
                                            </span>
                                            {isMobileMode || hasActiveItem ? (
                                                isOpen ? (
                                                    <MdExpandLess className="text-lg text-base-content/50" />
                                                ) : (
                                                    <MdExpandMore className="text-lg text-base-content/50" />
                                                )
                                            ) : (
                                                <MdExpandMore
                                                    className={`text-lg text-base-content/50 transition-transform duration-200 ${isFlyoutOpen ? '-rotate-90' : ''}`}
                                                />
                                            )}
                                        </button>

                                        {/* Accordion: mobile sempre, desktop quando módulo tem item ativo */}
                                        {(isMobileMode || hasActiveItem) &&
                                            isOpen && (
                                                <div className="ml-3 pl-3 border-l-2 border-base-300/50 space-y-0">
                                                    {filteredItems.map(item =>
                                                        renderMenuItem(
                                                            item,
                                                            filteredItems
                                                        )
                                                    )}
                                                </div>
                                            )}
                                    </div>
                                )
                            })}
                        </>
                    )}
                </div>
            </nav>

            {/* Flyout panel - desktop apenas */}
            {flyoutModule &&
                isDesktop &&
                !onItemClick &&
                (() => {
                    const activeModule = menuModules.find(
                        m => m.label === flyoutModule
                    )
                    if (!activeModule) return null
                    const filteredItems =
                        activeModule.items.filter(isItemVisible)
                    if (filteredItems.length === 0) return null

                    return (
                        <div
                            ref={flyoutRef}
                            className={`${themeClass} fixed z-[60] border ${isLightSidebar ? 'border-base-300' : 'border-white/10'} rounded-xl shadow-2xl py-2 min-w-[220px] max-h-[calc(100vh-24px)] overflow-y-auto transition-opacity duration-150`}
                            style={{
                                left: sidebarRef.current
                                    ? sidebarRef.current.getBoundingClientRect()
                                          .right + 4
                                    : 0,
                                top:
                                    flyoutAdjustedTop ??
                                    flyoutTop +
                                        (sidebarRef.current?.getBoundingClientRect()
                                            .top || 0),
                                opacity: flyoutAdjustedTop !== null ? 1 : 0,
                            }}
                        >
                            <div className="px-4 py-2 border-b border-base-300/50 mb-1 flex items-center justify-between gap-2">
                                <span className="text-sm font-semibold text-base-content/70">
                                    {activeModule.label}
                                </span>
                                {activeModule.moduleKey &&
                                    (() => {
                                        const isPinned = pinnedModules.includes(
                                            activeModule.moduleKey
                                        )
                                        const PinIcon = isPinned
                                            ? MdPushPin
                                            : MdOutlinePushPin
                                        return (
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    togglePinnedModule(
                                                        activeModule.moduleKey!
                                                    )
                                                }
                                                className={`btn btn-ghost btn-xs btn-circle ${isPinned ? 'text-secondary' : 'text-base-content/50 hover:text-base-content'}`}
                                                title={
                                                    isPinned
                                                        ? 'Desafixar módulo'
                                                        : 'Fixar módulo no topo'
                                                }
                                            >
                                                <PinIcon className="text-base" />
                                            </button>
                                        )
                                    })()}
                            </div>
                            <div className="px-2 space-y-0">
                                {filteredItems.map(item => {
                                    const Icon = item.icon
                                    const hrefPath = item.href.split('?')[0]
                                    const hrefSearch = item.href.includes('?')
                                        ? '?' + item.href.split('?')[1]
                                        : ''
                                    const isExactMatch = hrefSearch
                                        ? location.pathname === hrefPath &&
                                          location.search === hrefSearch
                                        : location.pathname === hrefPath
                                    const isPrefixMatch =
                                        hrefPath !== '/' &&
                                        hrefPath !== '/admin' &&
                                        !hrefSearch &&
                                        location.pathname.startsWith(
                                            hrefPath + '/'
                                        )
                                    const isActive =
                                        isExactMatch || isPrefixMatch

                                    return (
                                        <Link
                                            key={item.href}
                                            to={item.href}
                                            onClick={() =>
                                                setFlyoutModule(null)
                                            }
                                            className={`
                                            flex items-center gap-3 px-3 py-2.5 rounded-lg
                                            transition-all duration-150 group
                                            ${
                                                isActive
                                                    ? 'bg-secondary text-primary-content shadow-sm'
                                                    : 'text-base-content hover:bg-base-200/70'
                                            }
                                        `}
                                        >
                                            <Icon
                                                className={`text-lg flex-shrink-0 ${isActive ? 'text-primary-content' : 'text-base-content/60 group-hover:text-base-content'}`}
                                            />
                                            <span
                                                className={`text-sm ${isActive ? 'text-primary-content font-medium' : 'text-base-content'}`}
                                            >
                                                {item.label}
                                            </span>
                                        </Link>
                                    )
                                })}
                            </div>
                        </div>
                    )
                })()}

            <div
                className={`${collapsed ? 'p-2' : 'p-4'} bg-gradient-to-br from-secondary/10 to-secondary/10 border-t border-secondary/20`}
            >
                <div
                    className={`flex ${collapsed ? 'flex-col' : ''} gap-5 justify-center`}
                >
                    {orphanPaymentsCount > 0 && (
                        <button
                            onClick={() => navigate('/financial/orphan-payments')}
                            className="btn btn-sm btn-ghost btn-circle bg-error/10 hover:bg-error/20 relative"
                            onMouseEnter={e =>
                                showCollapsedTip(
                                    `${orphanPaymentsCount} pagamento(s) sem vínculo`,
                                    e.currentTarget,
                                )
                            }
                            onMouseLeave={hideCollapsedTip}
                            onFocus={e =>
                                showCollapsedTip(
                                    `${orphanPaymentsCount} pagamento(s) sem vínculo`,
                                    e.currentTarget,
                                )
                            }
                            onBlur={hideCollapsedTip}
                            aria-label={`${orphanPaymentsCount} pagamento(s) sem vínculo`}
                        >
                            <MdLinkOff className="text-error text-lg" />
                            <span className="absolute -top-1.5 -right-1.5 badge badge-error badge-xs text-white font-bold min-w-[1.1rem] px-1">
                                {orphanPaymentsCount > 99 ? '99+' : orphanPaymentsCount}
                            </span>
                        </button>
                    )}
                    {canAccessModule('financial') && (
                        <button
                            onClick={() => setShowBillingModal(true)}
                            className="btn btn-sm btn-ghost btn-circle bg-success/10 hover:bg-success/20"
                            onMouseEnter={e => showCollapsedTip('Nova cobrança', e.currentTarget)}
                            onMouseLeave={hideCollapsedTip}
                            onFocus={e => showCollapsedTip('Nova cobrança', e.currentTarget)}
                            onBlur={hideCollapsedTip}
                        >
                            <MdReceipt className="text-success text-lg" />
                        </button>
                    )}
                    {canAccessModule('bookings') && (
                        <div className="relative">
                            <button
                                onClick={e => {
                                    if (showMedicalDropdown) {
                                        setShowMedicalDropdown(false)
                                        return
                                    }
                                    // A posição é medida do botão e o menu sai
                                    // preso à JANELA: o rodapé mora dentro da rail,
                                    // que tem `overflow-hidden`, e um menu absoluto
                                    // ali era cortado pela borda — invisível quando
                                    // o menu está em ícones.
                                    const rect = e.currentTarget.getBoundingClientRect()
                                    // Abre PARA O LADO, alinhado pela base do botão,
                                    // igual ao flyout dos módulos. Centralizar sobre o
                                    // botão jogava o menu para fora da tela quando o
                                    // menu está em ícones: o centro fica a ~32px da
                                    // borda e metade de um painel de 180px é 90px.
                                    setMedicalDropdownPos({
                                        left: rect.right + 8,
                                        bottom: Math.max(8, window.innerHeight - rect.bottom),
                                    })
                                    setShowMedicalDropdown(true)
                                }}
                                className="btn btn-sm btn-ghost btn-circle bg-info/10 hover:bg-info/20"
                                onMouseEnter={e => showCollapsedTip('Agendamento rápido', e.currentTarget)}
                                onMouseLeave={hideCollapsedTip}
                                onFocus={e => showCollapsedTip('Agendamento rápido', e.currentTarget)}
                                onBlur={hideCollapsedTip}
                            >
                                <MdLocalHospital className="text-info text-lg" />
                            </button>
                            {showMedicalDropdown && (
                                <>
                                    <div
                                        className="fixed inset-0 z-[100]"
                                        onClick={() =>
                                            setShowMedicalDropdown(false)
                                        }
                                    />
                                    <div
                                        className="fixed z-[101] bg-base-100 rounded-lg shadow-xl border border-base-300 py-1 min-w-[180px]"
                                        style={{
                                            left: medicalDropdownPos?.left ?? 0,
                                            bottom: medicalDropdownPos?.bottom ?? 0,
                                        }}
                                    >
                                        <div className="px-3 py-1.5 text-xs font-semibold opacity-50 border-b border-base-200">
                                            Novo agendamento
                                        </div>
                                        {canRead('bookings', 'consultations') && (
                                            <button
                                                className="w-full text-left px-3 py-2 text-sm hover:bg-base-200 transition-colors"
                                                onClick={() => {
                                                    navigate(
                                                        '/atendimento/scheduling?type=CONSULTATION'
                                                    )
                                                    setShowMedicalDropdown(
                                                        false
                                                    )
                                                }}
                                            >
                                                {segment === 'HEALTH'
                                                    ? 'Consulta'
                                                    : 'Avaliação'}
                                            </button>
                                        )}
                                        {!HIDE_RESOURCE_BY_SECONDARY_SEGMENT[
                                            secondarySegment
                                        ].includes('teleconsultation') &&
                                            canRead('bookings', 'teleconsultations') && (
                                                <button
                                                    className="w-full text-left px-3 py-2 text-sm hover:bg-base-200 transition-colors"
                                                    onClick={() => {
                                                        navigate(
                                                            '/atendimento/scheduling?type=TELECONSULTATION'
                                                        )
                                                        setShowMedicalDropdown(
                                                            false
                                                        )
                                                    }}
                                                >
                                                    Teleconsulta
                                                </button>
                                            )}
                                        {canRead('bookings', 'exams') && (
                                            <button
                                                className="w-full text-left px-3 py-2 text-sm hover:bg-base-200 transition-colors"
                                                onClick={() => {
                                                    navigate(
                                                        '/atendimento/scheduling?type=EXAM'
                                                    )
                                                    setShowMedicalDropdown(
                                                        false
                                                    )
                                                }}
                                            >
                                                Exame
                                            </button>
                                        )}
                                        {canRead('bookings', 'orders') && (
                                            <button
                                                className="w-full text-left px-3 py-2 text-sm hover:bg-base-200 transition-colors"
                                                onClick={() => {
                                                    navigate(
                                                        '/atendimento/scheduling?type=PROCEDURE'
                                                    )
                                                    setShowMedicalDropdown(
                                                        false
                                                    )
                                                }}
                                            >
                                                {
                                                    getServiceLabels(segment)
                                                        .Order
                                                }
                                            </button>
                                        )}

                                        <div className="px-3 py-1.5 mt-1 text-xs font-semibold opacity-50 border-t border-b border-base-200">
                                            Localizar
                                        </div>
                                        <button
                                            className="w-full text-left px-3 py-2 text-sm hover:bg-base-200 transition-colors"
                                            onClick={() => {
                                                setShowSearchByNumber(true)
                                                setShowMedicalDropdown(false)
                                            }}
                                        >
                                            Buscar por número
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                    <div className="relative">
                        <button
                            onClick={e => {
                                if (themeMenuPos) {
                                    setThemeMenuPos(null)
                                    return
                                }
                                // Mesmo posicionamento do "Agendamento rápido":
                                // preso à janela, ao lado do botão.
                                const rect = e.currentTarget.getBoundingClientRect()
                                setThemeMenuPos({
                                    left: rect.right + 8,
                                    bottom: Math.max(8, window.innerHeight - rect.bottom),
                                })
                            }}
                            className="btn btn-sm btn-ghost btn-circle bg-base-content/5 hover:bg-base-content/10"
                            onMouseEnter={e => showCollapsedTip('Aparência do menu', e.currentTarget)}
                            onMouseLeave={hideCollapsedTip}
                            onFocus={e => showCollapsedTip('Aparência do menu', e.currentTarget)}
                            onBlur={hideCollapsedTip}
                            aria-label="Aparência do menu"
                            aria-expanded={!!themeMenuPos}
                        >
                            <MdSettings className="text-base-content/70 text-lg" />
                        </button>
                        {themeMenuPos && (
                            <>
                                <div
                                    className="fixed inset-0 z-[100]"
                                    onClick={() => setThemeMenuPos(null)}
                                />
                                <div
                                    className="fixed z-[101] bg-base-100 rounded-lg shadow-xl border border-base-300 p-2 w-[220px]"
                                    style={{
                                        left: themeMenuPos.left,
                                        bottom: themeMenuPos.bottom,
                                    }}
                                >
                                    <div className="px-2 pt-1 pb-2 text-xs font-semibold opacity-60">
                                        Aparência do menu
                                    </div>
                                    {(
                                        [
                                            { value: 'dark', label: 'Escuro', hint: 'Gradiente da marca' },
                                            { value: 'light', label: 'Claro', hint: 'Fundo branco' },
                                        ] as { value: SidebarTheme; label: string; hint: string }[]
                                    ).map(option => {
                                        const isSelected = sidebarTheme === option.value
                                        return (
                                            <button
                                                key={option.value}
                                                type="button"
                                                className={`w-full flex items-center gap-3 px-2 py-2 rounded-md text-left transition-colors ${isSelected ? 'bg-secondary/10' : 'hover:bg-base-200'}`}
                                                onClick={() => {
                                                    setSidebarTheme(option.value)
                                                    setThemeMenuPos(null)
                                                }}
                                            >
                                                {/* Amostra com a própria classe da rail: mostra o fundo real. */}
                                                <span
                                                    className={`sidebar-${option.value} w-8 h-8 rounded-md border border-base-300 flex-shrink-0`}
                                                />
                                                <span className="flex-1 min-w-0">
                                                    <span className="block text-sm font-medium">
                                                        {option.label}
                                                    </span>
                                                    <span className="block text-xs opacity-60">
                                                        {option.hint}
                                                    </span>
                                                </span>
                                                {isSelected && (
                                                    <MdCheck className="text-secondary text-lg flex-shrink-0" />
                                                )}
                                            </button>
                                        )
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
          </div>
          {/* Fim da rail — os modais abaixo seguem no tema claro padrão. */}

            {/* Busca por número (id de exame/procedimento/consulta/cobrança) */}
            {showSearchByNumber && (
                <SearchByNumberModal
                    onClose={() => setShowSearchByNumber(false)}
                />
            )}

            {/* Billing Modal (PDV rápido) */}
            {showBillingModal && (
                <div className="modal modal-open" style={{ zIndex: 9999 }}>
                    <div className="modal-box !max-w-4xl h-[90vh] p-0 flex flex-col overflow-hidden">
                        <div className="flex-1 overflow-y-auto p-6">
                            <BillingsFormPage
                                isModal
                                defaultType="RECEIVABLE"
                                onBack={() => setShowBillingModal(false)}
                                onSuccess={billingId => {
                                    setShowBillingModal(false)
                                    // Fechar não basta: o operador espera cair nos
                                    // detalhes da cobrança recém-criada (ver o PIX,
                                    // enviar o link). Sem id, mantém o antigo
                                    // comportamento de só fechar.
                                    if (billingId) {
                                        navigate(
                                            `/financial/billings/${billingId}`
                                        )
                                    }
                                }}
                            />
                        </div>
                    </div>
                    <form
                        method="dialog"
                        className="modal-backdrop"
                        onClick={() => setShowBillingModal(false)}
                    >
                        <button>close</button>
                    </form>
                </div>
            )}

            {collapsed &&
                collapsedTip &&
                createPortal(
                    <div
                        role="tooltip"
                        className="pointer-events-none fixed z-[1000] -translate-y-1/2 whitespace-nowrap rounded-md bg-neutral px-2.5 py-1.5 text-xs font-medium text-neutral-content shadow-lg"
                        style={{ top: collapsedTip.top, left: collapsedTip.left }}
                    >
                        <span className="absolute -left-1 top-1/2 h-2 w-2 -translate-y-1/2 rotate-45 bg-neutral" />
                        {collapsedTip.label}
                    </div>,
                    document.body
                )}
        </aside>
    )
}
